const IMetricsClient = require("./IMetricsClient.js");
const DEFAULT_ALLOWED_ATTRIBUTES = require("./defaultAllowedAttributes.js");

const FLUSH_TIMEOUT_MS = 2000;

/**
 * Browser-side metrics client backed by Sentry's Trace Metrics API
 * (`Sentry.metrics.*`). Inject `@sentry/react`.
 *
 * Each tracked event is recorded as a counter via `metrics.count()`, so events
 * land in Sentry's metrics product — NOT the error/issue stream. Allowlisted
 * event properties are attached as metric attributes for querying.
 *
 * Unlike {@link SentryNodeClient}, this client attaches user identity to the
 * Sentry scope: `identify` calls `setUser` and `group` calls `setContext`. That
 * is deliberate, and the reason the two runtimes are separate classes.
 *
 * Trace Metrics are enabled by default in the Sentry SDK (v10+); no special
 * `Sentry.init()` flag is required.
 */
class SentryBrowserClient extends IMetricsClient {
  /**
   * @param {object} sentry The Sentry SDK (e.g. `@sentry/react` / `@sentry/node`).
   * @param {object} [options]
   * @param {string[]} [options.allowedAttributes] Override the attribute
   *   allowlist applied in `track`. Defaults to DEFAULT_ALLOWED_ATTRIBUTES.
   * @param {(error: Error) => void} [options.onError] See {@link IMetricsClient}.
   */
  constructor(
    sentry,
    { allowedAttributes = DEFAULT_ALLOWED_ATTRIBUTES, onError } = {}
  ) {
    super({ onError });
    this.sentry = sentry;
    this.allowedAttributes = new Set(allowedAttributes);
  }

  /**
   * Record an event as a Sentry counter. Event properties are filtered to the
   * attribute allowlist (DB-derivable ids/descriptors, sensitive values and the
   * acting user id are dropped). `userId` is accepted for interface parity but
   * deliberately not emitted — there is no per-user attribution in the metrics
   * product.
   */
  track(userId, eventName, properties = {}) {
    if (
      !this.sentry ||
      !this.sentry.metrics ||
      typeof this.sentry.metrics.count !== "function"
    )
      return;

    const attributes = {};
    const props = properties || {};
    for (const key of Object.keys(props)) {
      if (this.allowedAttributes.has(key)) attributes[key] = props[key];
    }

    this.safeInvoke(() =>
      this.sentry.metrics.count(eventName, 1, { attributes })
    );
  }

  /**
   * Associate the acting user with the Sentry scope. Only the user id is sent —
   * `traits` (email / name / team metadata, etc.) are deliberately NOT forwarded
   * so the migration never leaks PII into Sentry. The id is enough to correlate
   * a user with their events/errors; richer attributes live in the DB.
   */
  // oxlint-disable-next-line no-unused-vars
  identify(userId, traits = {}) {
    if (!userId || !this.sentry || typeof this.sentry.setUser !== "function")
      return;
    this.safeInvoke(() => this.sentry.setUser({ id: userId }));
  }

  /**
   * Associate the acting user with a group on the Sentry scope. Only the group
   * and user identifiers are sent — `groupTraits` (team name, plan metadata,
   * etc.) are deliberately NOT forwarded, matching `identify()`, so the
   * migration never leaks PII into Sentry. Descriptive group attributes live in
   * the DB.
   */
  // oxlint-disable-next-line no-unused-vars
  group(groupId, userId, groupTraits = {}) {
    if (!this.sentry || typeof this.sentry.setContext !== "function") return;
    this.safeInvoke(() =>
      this.sentry.setContext("group", { id: groupId, userId })
    );
  }

  async flush() {
    if (this.sentry && typeof this.sentry.flush === "function") {
      await this.safeInvoke(() => this.sentry.flush(FLUSH_TIMEOUT_MS));
    }
  }
}

module.exports = SentryBrowserClient;
module.exports.DEFAULT_ALLOWED_ATTRIBUTES = DEFAULT_ALLOWED_ATTRIBUTES;
